import { useRef, useState } from "react";
import { FileVideo, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, fmtBytes, uploadFileChunked } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

export const UploadDropzone = ({ onUploaded }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState(null);
  const [drag, setDrag] = useState(false);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const upload = async () => {
    setProgress(0);
    try {
      const uploadId = await uploadFileChunked(file, { onProgress: setProgress });
      const { data } = await api.post("/projects/from-upload", { upload_id: uploadId, title });
      toast.success("Upload complete — analysing source");
      onUploaded(data);
    } catch (e) {
      toast.error(errorMessage(e, "Upload failed"));
      setProgress(null);
    }
  };

  return (
    <div className="card-surface p-6 sm:p-8 space-y-5" data-testid="upload-panel">
      <span className="overline-label">Step 1 · Source video</span>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
        data-testid="upload-dropzone"
      >
        <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv,.m4v" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="upload-file-input" />
        {file ? (
          <div className="flex flex-col items-center gap-2">
            <FileVideo className="text-primary" size={30} />
            <p className="font-semibold" data-testid="upload-file-name">{file.name}</p>
            <p className="text-xs font-mono text-muted-foreground">{fmtBytes(file.size)}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <UploadCloud className="text-muted-foreground" size={30} />
            <p className="font-semibold">Drop a video or click to browse</p>
            <p className="text-xs text-muted-foreground">MP4, MOV, WEBM, MKV · up to 500 MB · up to 15 minutes</p>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Project title" className="h-11 rounded-xl" data-testid="upload-title-input" />
        <Button onClick={upload} disabled={!file || progress !== null} className="btn-pill h-11 px-6" data-testid="upload-submit-button">
          {progress === null ? "Upload & analyse" : `Uploading ${progress}%`}
        </Button>
      </div>
      {progress !== null && <Progress value={progress} className="h-1.5" data-testid="upload-progress" />}
    </div>
  );
};
